import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ElementRef,
  OnInit,
  OnChanges,
  OnDestroy
} from '@angular/core'
import { AbcInput } from '../../../common/interfaces/abc-input.interface'
import { environment } from '../../../../environments/environment'
import { ValidatorFn, Validators, FormGroup, FormBuilder } from '@angular/forms'
import { ImageSet } from '../../../common/interfaces/image-set.interface'
import { Observable } from 'rxjs'

declare const navigator: any;
declare const window: any;

@Component({
  selector: 'abc-image-input',
  templateUrl: './image-input.component.html',
  styleUrls: ['./image-input.component.scss']
})

export class ImageInputComponent implements AbcInput, OnInit, OnChanges, OnDestroy {
  @Input() label: string
  @Input() placeholder: string
  @Input() helpText: string
  @Input() initialValue: ImageSet
  @Input() initialBlob: Blob
  @Input() nativeFileUploaded = false
  @Input() accept: string
  @Input() showErrors = false
  @Input() validators: ValidatorFn[] = []
  @Input() uniqueId: string
  @Input() reset: Observable<void>

  blobUrl: string

  @Output() valueChanged: EventEmitter<{
    name: string
    content: File
  }> = new EventEmitter()

  @ViewChild('imageInput') imageInputEl: ElementRef

  image: { name: string; content: File }
  previouslyUploadedImage: ImageSet
  storagePath: string = environment.storagePath

  form: FormGroup
  required: boolean
  private devReady = false;
  private takingPhoto = false;


  constructor(private formBuilder: FormBuilder) {}
  private async ensurePermissions() {
    const perms = (window as any).cordova && (window as any).cordova.plugins ? (window as any).cordova.plugins.permissions : null;
    if (!perms) return;
  
    const needed = [
      'android.permission.CAMERA',
      'android.permission.READ_MEDIA_IMAGES',
      'android.permission.READ_EXTERNAL_STORAGE',
    ];
    await new Promise<void>((resolve, reject) => {
      perms.requestPermissions(needed, () => resolve(), (e: any) => resolve());
    });
  }
  
  private fileUriToBlob(uri: string, fallbackName = 'photo.jpg'): Promise<{ blob: Blob; name: string }> {
    return new Promise((resolve, reject) => {
      (window as any).resolveLocalFileSystemURL(uri, (entry: any) => {
        entry.file((f: File) => {
          const name = (f as any).name || fallbackName;
          const type = f.type || 'image/jpeg';
          const reader = new FileReader();
          reader.onload = () => {
            const buf = reader.result as ArrayBuffer;
            const blob = new Blob([buf], { type });
            resolve({ blob, name });
          };
          reader.onerror = reject;
          reader.readAsArrayBuffer(f);
        }, reject);
      }, reject);
    });
  }
  
  ngOnInit() {
    if (this.initialValue) {
      this.previouslyUploadedImage = this.initialValue
    }

    if (this.reset) {
      this.reset.subscribe(() => this.removeFile())
    }
    document.addEventListener('deviceready', () => this.devReady = true, false);
  }

  onLabelTap(evt: Event) {
    if (this.isCordovaCameraAvailable()) {
      evt.preventDefault();
      evt.stopPropagation();
      this.takePhotoViaCordova();
    }
  }

  isCordovaCameraAvailable(): boolean {
    return !!(window.cordova && navigator.camera) && this.devReady;
  }

  ngOnChanges(changes) {
    this.form = this.formBuilder.group({
      image: [this.initialValue, this.validators || []]
    })
    if (!this.initialValue && !this.nativeFileUploaded && !this.initialBlob) {
      this.removeFile()
    }
    if (this.nativeFileUploaded) {
      this.image = { name: 'Image importée', content: null }
    }

    if (changes && changes.initialBlob) {
      // Use a data URL (FileReader) instead of URL.createObjectURL — the Cordova
      // WebView blocks blob: URLs as <img src> (CSP / sandbox), data: URLs work.
      this.blobUrl = null
      if (this.initialBlob) {
        const reader = new FileReader()
        reader.onload = () => {
          this.blobUrl = reader.result as string
        }
        reader.readAsDataURL(this.initialBlob)
      }
    }

    this.required = this.validators.includes(Validators.required)
  }

  ngOnDestroy() {
    // data URLs do not need revoking, just drop the reference
    this.blobUrl = null
  }

  onClickFileInput(evt: MouseEvent) {
    if (this.isCordovaCameraAvailable()) {
      evt.preventDefault();
      evt.stopPropagation();
      this.takePhotoViaCordova();
    }
  }

  async takePhotoViaCordova() {
    if (this.takingPhoto) return;
    this.takingPhoto = true;
    try {
      await this.ensurePermissions();
  
      const opts = {
        destinationType: (navigator as any).camera.DestinationType.FILE_URI,
        sourceType: (navigator as any).camera.PictureSourceType.CAMERA,
        encodingType: (navigator as any).camera.EncodingType.JPEG,
        mediaType: (navigator as any).camera.MediaType.PICTURE,
        allowEdit: false,
        quality: 80,
        targetWidth: 1920,
        targetHeight: 1920,
        correctOrientation: true,
        saveToPhotoAlbum: true
      };
  
      const uri: string = await new Promise((resolve, reject) => {
        (navigator as any).camera.getPicture(resolve, (e: any) => reject(e), opts);
      });
  
      const { blob, name } = await this.fileUriToBlob(uri, `photo_${Date.now()}.jpg`);
      this.image = { name, content: blob as any as File };
      if (this.form.get('image'))
        this.form.get('image').setValue(this.image);
      this.initialValue = { changed: true };
      this.valueChanged.emit(this.image);
    } catch (e) {
      console.warn('Camera annulée/erreur :', e);
    } finally {
      try {
        (navigator as any).camera.cleanup ? (navigator as any).camera.cleanup(() => {}, () => {}) : null;
      } catch {}
      this.takingPhoto = false;
    }
  }
  
  private async base64ToFile(base64NoHeader: string, fileName: string, mime = 'image/jpeg'): Promise<File> {
    const dataUrl = `data:${mime};base64,${base64NoHeader}`;
    const res = await fetch(dataUrl);
    const blob = await res.blob();
  
    try {
      const f = new File([blob], fileName, { type: mime, lastModified: Date.now() });
      if (f.size && f.size > 0) return f;
    } catch {}

    (blob as any).name = fileName;
    (blob as any).lastModified = Date.now();
    return blob as any as File;
  }

  imageInputEvent(ev: any) {
    const files = ev && ev.target && ev.target.files;
    if (!files || !files[0]) return;
    this.image = { name: files[0].name, content: files[0] };
    if (this.form.get('image'))
      this.form.get('image').setValue(this.image);
    this.initialValue = { changed: true };
    this.valueChanged.emit(this.image);
  }

  removeFile() {
    delete this.image
    delete this.previouslyUploadedImage
    this.blobUrl = null
    this.initialBlob = null
    this.nativeFileUploaded = false
    this.form.reset()
    this.valueChanged.emit(null)
  }
}
